// libs
import React, { useCallback, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'

// actions

// components
import AvatarName from 'src/components/avatar/avatar_name'
import BackbuttonHeader from 'src/components/header/common/backbutton_header/backbutton_header'
import MoreMenu from 'src/components/header/subheaders/shared_contents/more_menu/more_menu'
import MenuItemSegment from 'src/components/item_segment/layouts/menu/menu'
import {
  MSGBOX_BUTTON_TYPE_CANCEL,
  MSGBOX_BUTTON_TYPE_DANGER,
  MSGBOX_BUTTON_TYPE_PRIMARY
} from 'src/components/message_box/message_box'
import MessageBoxWithModal from 'src/components/message_box/message_box_with_modal'
import { showSnackBar } from 'src/components/snack_bar/snack_bar'
import { THUMBNAIL_SIZE_TYPE_XXSMALL } from 'src/components/thumbnail/thumbnail'
import UserOnboardingCTA from 'src/containers/smart_components/user_onboarding_cta/user_onboarding_cta'

// interfaces / constants
import { CLASS_PREFIX } from 'src/constants/'
import { IMoreOption, MORE_OPTIONS } from 'src/constants/more_button'

// helpers
import { useGoToNewsFeed } from 'src/hooks_shared/use_navigation'
import { textResources } from 'src/lang/de'
import { useSelectCurrentProfile, useSelectIsUserLoggedIn } from 'src/reducers/user/user_hooks'
import { COLOR_TYPE_BLACK } from 'src/utils/color'
import { UrlUtils } from 'src/utils/url/url'

import { ActorProfileDetailFragmentFragment, UserQuery, useBlockProfile, useUnblockProfile } from '@lopo-tech/lopo-api'
import {
  isMessengerFlagEnabled, isUserMessagable
} from '../../../../settings/settings_wizard/steps/settings_overview/messenger_toggle/helpers'
import './header_profile.scss'

const cls: string = CLASS_PREFIX + 'header_profile'
const clsMoreWrapper = `${cls}__more-wrapper`

const labels = textResources.blacklist

const shouldAllowMessaging = (
  profileDetail: ActorProfileDetailFragmentFragment,
  myProfile?: UserQuery['currentProfile']
) => {
  if (!isMessengerFlagEnabled()) {
    return true
  }

  if (!myProfile) {
    return false
  }

  // TODO use myProfile.isMessengerEnabled once it has been added to the gql api
  if (!isUserMessagable(myProfile)) {
    return false
  }

  return profileDetail.canMessage.value
}

interface IProps {
  profile: ActorProfileDetailFragmentFragment | null | undefined
}

const HeaderProfile = ({ profile }: IProps) => {
  const isNotLoggedIn = !useSelectIsUserLoggedIn()
  const myProfile = useSelectCurrentProfile()
  const history = useHistory()
  const goToNewsFeed = useGoToNewsFeed()
  const [showBlackList, setShowBlackList] = useState(false)
  const [showRevertBlackList, setShowRevertBlackList] = useState(false)

  const [blockProfile] = useBlockProfile()
  const [unblockProfile] = useUnblockProfile()

  const closeBlackListMessageBox = () => setShowBlackList(false)
  const closeRevertBlackListMessageBox = () => setShowRevertBlackList(false)
  const isCurrentUserProfile = profile?.id === myProfile?.id

  const options: IMoreOption[] = useMemo((): IMoreOption[] => {
    const options: IMoreOption[] = []

    if (profile) {
      if (shouldAllowMessaging(profile, myProfile)) {
        options.push(MORE_OPTIONS.MESSAGE)
      }
      if (profile?.canBlock.value) {
        options.push(MORE_OPTIONS.BLACKLIST)
      }
      if (profile?.canUnblock.value) {
        options.push(MORE_OPTIONS.REVERT_BLACKLIST)
      }
    }
    return options
  }, [profile, myProfile])

  const handleBackButton = useCallback(() => UrlUtils.goBack(history), [history])

  const moreButtonAction = useCallback((option: IMoreOption) => {
    if (option.value === MORE_OPTIONS.BLACKLIST.value) {
      setShowBlackList(true)
    }
    if (option.value === MORE_OPTIONS.REVERT_BLACKLIST.value) {
      setShowRevertBlackList(true)
    }
    if (option.value === MORE_OPTIONS.MESSAGE.value) {
      if (!profile?.__typename) return

      const id = profile?.id || ''
      history.push(
        UrlUtils.sendMessageConversationPage(profile.__typename, id)
      )
    }
  }, [history, profile])

  const handleBlacklist = useCallback(async () => {
    closeBlackListMessageBox()
    if (!profile) return

    try {
      await blockProfile({
        variables: { input: { id: profile.id } }
      })

      goToNewsFeed()
    } catch (errors) {
      showSnackBar(errors.message)
    }
  }, [profile, goToNewsFeed])

  const handleRevertBlacklist = useCallback(async () => {
    closeRevertBlackListMessageBox()
    if (!profile) return

    try {
      await unblockProfile({
        variables: { input: { id: profile.id } },
        awaitRefetchQueries: true
      })
    } catch (errors) {
      showSnackBar(errors.message)
    }
  }, [profile])

  return (
    <BackbuttonHeader
      handleBackButton={handleBackButton}
      title={
        <AvatarName
          size={THUMBNAIL_SIZE_TYPE_XXSMALL}
          name={profile?.name}
          image={profile?.avatar.previewUrl}
          rounded={false}
        />
      }
    >
      {profile && !isCurrentUserProfile && (
        <UserOnboardingCTA active={isNotLoggedIn} gtmSourceName='profileHeader--more' className={clsMoreWrapper}>
          <div className={clsMoreWrapper}>
            {options.length > 0 && (
              <MoreMenu>
                {options.map(option => (
                  <MenuItemSegment
                    key={option.value}
                    label={option.text}
                    navigation={{ onClick: () => { moreButtonAction(option) } }}
                    icon={{ color: COLOR_TYPE_BLACK, name: option.icon }}
                  />
                ))}
              </MoreMenu>
            )}
          </div>
        </UserOnboardingCTA>)
      }
      {showBlackList && (
        <MessageBoxWithModal
          buttons={[
            {
              label: labels.blacklist,
              onClick: handleBlacklist,
              type: MSGBOX_BUTTON_TYPE_DANGER
            },
            {
              onClick: closeBlackListMessageBox,
              type: MSGBOX_BUTTON_TYPE_CANCEL
            }
          ]}
          onClose={closeBlackListMessageBox}
          title={labels.modalTitle}
        >
          <p>{labels.modalContent}</p>
        </MessageBoxWithModal>
      )}
      {showRevertBlackList && (
        <MessageBoxWithModal
          buttons={[
            {
              label: labels.revertBlacklist,
              onClick: handleRevertBlacklist,
              type: MSGBOX_BUTTON_TYPE_PRIMARY
            },
            {
              onClick: closeRevertBlackListMessageBox,
              type: MSGBOX_BUTTON_TYPE_CANCEL
            }
          ]}
          onClose={closeRevertBlackListMessageBox}
          title={labels.modalTitleRemove}
        />
      )}
    </BackbuttonHeader>
  )
}

/* set displayname to not have a component without a name */
HeaderProfile.displayName = 'HeaderProfile'

export default HeaderProfile
